import React, { useState } from 'react';
import { X, Loader2, Download, Wand2, Copy, FileText, File as FilePdf, ExternalLink } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { toast } from '../lib/toast';
import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, HeadingLevel, TextRun } from 'docx';
import { supabase } from '../lib/supabase';

interface ProjectCharterModalProps {
  onClose: () => void;
}

interface ChapterContent {
  title: string;
  content: string[];
}

interface SavedOutput {
  id: string;
  saved: boolean;
}

export default function ProjectCharterModal({ onClose }: ProjectCharterModalProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [parsedContent, setParsedContent] = useState<ChapterContent[]>([]);
  const [showTemplate, setShowTemplate] = useState(false);
  const [savedOutput, setSavedOutput] = useState<SavedOutput | null>(null);
  const [savingOutput, setSavingOutput] = useState(false);

  const parseContent = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const chapters: ChapterContent[] = [];
    let currentChapter: ChapterContent | null = null;

    doc.body.childNodes.forEach((node) => {
      if (node.nodeName === 'H2') {
        if (currentChapter) {
          chapters.push(currentChapter);
        }
        currentChapter = {
          title: node.textContent || '',
          content: []
        };
      } else if (currentChapter && node.textContent?.trim()) {
        currentChapter.content.push(node.textContent.trim());
      }
    });

    if (currentChapter) {
      chapters.push(currentChapter);
    }

    return chapters;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      toast.error('Please enter project details');
      return;
    }

    setLoading(true);
    try {
      const prompt = `Create a detailed project charter in the following format (use this exact structure and formatting):

      AI-Generated Project Charter
      Project Name:
      [Project name derived from input]
      
      Project Sponsor:
      [Key stakeholder/organization]
      
      Project Manager:
      [Identified from input or suggest role]
      
      Project Overview:
      [Detailed overview of the project including its purpose, goals, and scope]
      
      Scope of Work:
      In-Scope Features:
      [Bullet list of features that are included in the project scope]
      
      Out of Scope:
      [Bullet list of features that are explicitly excluded from the project scope]
      
      Key Benefits:
      [List major benefits]
      
      Stakeholders & Team Members:
      [List key stakeholders and team composition]
      
      Project Timeline & Milestones:
      [Detailed timeline with phases, start dates, end dates, and descriptions]
      
      Budget & Resources:
      [Budget details and resource requirements]
      
      Risks & Mitigation Strategies:
      [List of risks with likelihood and mitigation plans]
      
      Success Criteria:
      [List of measurable success criteria]
      
      Approval & Next Steps:
      [Information about approval process and next steps]
      
      For the following project details:
      ${input}

      Format the response exactly like this example:
      
      AI-Generated Project Charter
      Project Name:
      SmartFit – AI-Powered Fitness Tracking App
      
      Project Sponsor:
      Company Leadership
      
      Project Manager:
      CTO
      
      Project Overview:
      The SmartFit mobile application aims to help users track their fitness journey by offering step counting, calorie tracking, and personalized workout reminders. The app will allow users to set fitness goals, monitor progress, and sync with smartwatches for real-time tracking. The focus is on creating a simple, user-friendly interface to ensure ease of use across different age groups.
      
      The application will be developed for both iOS and Android platforms, ensuring a wide user reach. AI-driven notifications will provide motivation and personalized reminders based on user activity.
      
      Scope of Work:
      In-Scope Features:
      Step counter and calorie tracking
      Customizable fitness goals and progress tracking
      AI-generated workout reminders
      Smartwatch integration
      Push notifications for user engagement
      
      Out of Scope:
      Advanced AI coaching or workout recommendations
      Live personal training sessions
      Integration with third-party health insurance platforms
      
      Key Benefits:
      Encourages users to stay active through automated reminders
      Tracks daily activities and calorie consumption for better health insights
      Provides real-time synchronization with smartwatches for accurate fitness tracking
      Helps users stay accountable to their goals with easy progress monitoring
      
      Stakeholders & Team Members:
      The primary users of the app will be fitness enthusiasts, beginners, and individuals looking for a structured way to track their daily activities.
      
      The project team consists of:
      1 Mobile Developer (iOS & Android development)
      1 Backend Developer (Data storage, smartwatch integration)
      1 UI/UX Designer (Creating a simple and engaging user interface)
      The CTO will oversee project execution and ensure alignment with business objectives.
      
      Project Timeline & Milestones:
      The development of SmartFit is planned over six months, divided into key phases:
      
      Month 1: Research & Planning – Define app features, finalize technology stack, research smartwatch APIs.
      
      Month 2-3: UI/UX Design & Development – Design user-friendly interfaces, start mobile development.
      
      Month 4: AI & Notification System Implementation – Develop AI-based workout reminders, integrate push notifications.
      
      Month 5: Testing & Smartwatch Integration – Conduct extensive user testing, refine features, sync with smartwatches.
      
      Month 6: Deployment & Optimization – Launch the app, gather user feedback, and release updates.
      
      Budget & Resources:
      The estimated budget is not finalized, but the goal is to develop a cost-effective solution using React Native for cross-platform development and Firebase for backend services.
      
      Risks & Mitigation Strategies:
      Data Privacy Concerns – Implement strong encryption for user data, comply with GDPR and other relevant policies.
      
      User Engagement Challenges – AI-driven motivational notifications and personalized reminders to keep users active.
      
      Smartwatch Compatibility Issues – Early testing with multiple smartwatch brands to ensure smooth integration.
      
      Success Criteria:
      At least 10,000 app downloads in the first 6 months
      80% of users actively using the app for goal tracking
      User engagement increase by 30% through AI-driven notifications
      Positive feedback from at least 75% of early adopters
      
      Approval & Next Steps:
      This project is awaiting final approval from company leadership. Once approved, the development team will start the Research & Planning phase, followed by design and implementation.`;

      const response = await generateTaskDescription(prompt, 'task');
      setResult(response);
      setParsedContent(parseContent(response));

      // Prepare for saving but don't save automatically
      setSavedOutput({
        id: crypto.randomUUID(),
        saved: false
      });
    } catch (error) {
      console.error('Error generating charter:', error);
      toast.error('Failed to generate project charter');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!result || !savedOutput || savedOutput.saved) return;
    
    setSavingOutput(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save outputs');
        setSavingOutput(false);
        return;
      }
      
      // First try to find the agent by ID
      const { data: existingAgent, error: fetchError } = await supabase
        .from('ai_agents')
        .select('id')
        .eq('id', '00000000-0000-0000-0000-000000000002') 
        .limit(1)
        .maybeSingle();
      
      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Error fetching agent:', fetchError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      let agentId: string;
      
      if (!existingAgent) {
        // Try to find by name
        const { data: agentByName, error: nameError } = await supabase
          .from('ai_agents')
          .select('id')
          .eq('name', 'Project Charter Generator')
          .limit(1)
          .maybeSingle();
          
        if (nameError && nameError.code !== 'PGRST116') {
          console.error('Error finding agent by name:', nameError);
          toast.error('Failed to save output');
          setSavingOutput(false);
          return;
        }
        
        if (agentByName) {
          agentId = agentByName.id;
        } else {
          // Create new agent
          const { data: newAgent, error: createError } = await supabase
            .from('ai_agents')
            .insert({
              name: 'Project Charter Generator',
              user_id: user.id,
              goals: 'Generate comprehensive project charters',
              system_prompt: 'You are an expert project charter creator. Create detailed project charters based on project information.',
              is_custom: false,
              agent_type: 'text'
            })
            .select('id')
            .single();
            
          if (createError) {
            console.error('Error creating agent:', createError);
            toast.error('Failed to create agent');
            setSavingOutput(false);
            return;
          }
          
          agentId = newAgent.id;
        }
      } else {
        agentId = existingAgent.id;
      }
      
      // Save output to ai_agent_outputs table
      const { error: insertError } = await supabase.from('ai_agent_outputs').insert({
        user_id: user.id,
        agent_id: agentId,
        prompt: input,
        output_url: result,
        generation_type: 'text',
        llm_model: 'gpt-3.5-turbo',
        metadata: {},
        agent_type: 'text'
      });
      
      if (insertError) {
        console.error('Error saving output:', insertError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      toast.success('Output saved successfully');
      setSavedOutput({...savedOutput, saved: true});
    } catch (error) {
      console.error('Error saving agent output:', error);
      toast.error('Failed to save output');
    } finally {
      setSavingOutput(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result || '');
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const downloadPDF = () => {
    if (!parsedContent.length) return;

    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.width;
    const margin = 20;
    let yPos = 20;

    // Add title
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('AI-Generated Project Charter', margin, yPos);
    yPos += 15;

    parsedContent.forEach((chapter) => {
      // Check if we need a new page
      if (yPos > pdf.internal.pageSize.height - 40) {
        pdf.addPage();
        yPos = 20;
      }

      // Set chapter title font (smaller font)
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      
      // Add title
      pdf.text(chapter.title, margin, yPos);
      yPos += 8;

      // Set content font
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      chapter.content.forEach((line) => {
        // Check if we need a new page
        if (yPos > pdf.internal.pageSize.height - 20) {
          pdf.addPage();
          yPos = 20;
        }

        const lines = pdf.splitTextToSize(line, pageWidth - (margin * 2));
        lines.forEach((splitLine: string) => {
          pdf.text(splitLine, margin, yPos);
          yPos += 6;
        });
        yPos += 2; // Add a bit more space between paragraphs
      });
      
      yPos += 5; // Add space between sections
    });

    pdf.save('project-charter.pdf');
  };

  const downloadWord = async () => {
    if (!parsedContent.length) return;

    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: "Project Charter",
                bold: true,
                size: 32
              })
            ],
            heading: HeadingLevel.TITLE,
          }),
          ...parsedContent.flatMap(chapter => [
            new Paragraph({
              children: [
                new TextRun({
                  text: chapter.title,
                  bold: true,
                  size: 24
                })
              ],
              heading: HeadingLevel.HEADING_1,
            }),
            ...chapter.content.map(content => 
              new Paragraph({
                children: [
                  new TextRun({
                    text: content,
                    size: 20
                  })
                ]
              })
            )
          ])
        ]
      }]
    });

    Packer.toBlob(doc).then(blob => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'project-charter.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  };

  const handleDownloadTemplate = () => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      // Add header
      pdf.setFillColor(79, 70, 229);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      const title = 'Project Charter Template';
      const titleWidth = pdf.getStringUnitWidth(title) * 24 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 28);
      
      let yPos = 50;
      
      // Example Input section
      pdf.setTextColor(0, 0, 0);
      pdf.setFillColor(240, 244, 248);
      pdf.rect(margin, yPos - 6, contentWidth, 10, 'F');
      pdf.setFontSize(14);
      pdf.text('Example User Input', margin, yPos);
      
      yPos += 15;
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const exampleInput = "Need a mobile app for fitness tracking\nShould have step counter, calorie tracking, and workout reminders\nUsers can set fitness goals and see progress\nSync with smartwatches\nNeed it on iOS and Android\nSimple UI, easy to use\nBudget not decided yet, but should be cost-effective\nSmall team: 1 mobile developer, 1 backend developer, 1 UI/UX designer\nWant the app live in 6 months\nConcerns: data privacy, user engagement\nShould send notifications to keep users motivated";
      const wrappedExample = pdf.splitTextToSize(exampleInput, contentWidth);
      pdf.text(wrappedExample, margin, yPos);
      
      yPos += wrappedExample.length * 7 + 20;
      
      // Add example output
      pdf.addPage();
      yPos = 20;
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Example Output:', margin, yPos);
      yPos += 15;
      
      // Project Charter content
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AI-Generated Project Charter', margin, yPos);
      yPos += 12;
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Name:', margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('SmartFit – AI-Powered Fitness Tracking App', margin, yPos);
      yPos += 12;
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Sponsor:', margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Company Leadership', margin, yPos);
      yPos += 12;
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Manager:', margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('CTO', margin, yPos);
      yPos += 12;
      
      // Continue with other sections
      const sections = [
        {
          title: 'Project Overview:',
          content: [
            'The SmartFit mobile application aims to help users track their fitness journey by offering step counting, calorie tracking, and personalized workout reminders. The app will allow users to set fitness goals, monitor progress, and sync with smartwatches for real-time tracking. The focus is on creating a simple, user-friendly interface to ensure ease of use across different age groups.',
            'The application will be developed for both iOS and Android platforms, ensuring a wide user reach. AI-driven notifications will provide motivation and personalized reminders based on user activity.'
          ]
        },
        {
          title: 'Scope of Work:',
          content: [
            'In-Scope Features:',
            'Step counter and calorie tracking',
            'Customizable fitness goals and progress tracking',
            'AI-generated workout reminders',
            'Smartwatch integration',
            'Push notifications for user engagement',
            '',
            'Out of Scope:',
            'Advanced AI coaching or workout recommendations',
            'Live personal training sessions',
            'Integration with third-party health insurance platforms'
          ]
        },
        {
          title: 'Key Benefits:',
          content: [
            'Encourages users to stay active through automated reminders',
            'Tracks daily activities and calorie consumption for better health insights',
            'Provides real-time synchronization with smartwatches for accurate fitness tracking',
            'Helps users stay accountable to their goals with easy progress monitoring'
          ]
        }
      ];
      
      // Add first page of sections
      sections.forEach(section => {
        // Check if we need a new page
        if (yPos > pdf.internal.pageSize.height - 30) {
          pdf.addPage();
          yPos = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(section.title, margin, yPos);
        yPos += 8;
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        section.content.forEach(line => {
          // Check if we need a new page
          if (yPos > pdf.internal.pageSize.height - 20) {
            pdf.addPage();
            yPos = 20;
          }
          
          if (line) {
            const wrappedText = pdf.splitTextToSize(line, pageWidth - (margin * 2));
            pdf.text(wrappedText, margin, yPos);
            yPos += wrappedText.length * 6;
          } else {
            yPos += 6; // Empty line
          }
        });
        
        yPos += 10; // Space between sections
      });
      
      // Add second page of sections
      pdf.addPage();
      yPos = 20;
      
      const moreSections = [
        {
          title: 'Stakeholders & Team Members:',
          content: [
            'The primary users of the app will be fitness enthusiasts, beginners, and individuals looking for a structured way to track their daily activities.',
            '',
            'The project team consists of:',
            '1 Mobile Developer (iOS & Android development)',
            '1 Backend Developer (Data storage, smartwatch integration)',
            '1 UI/UX Designer (Creating a simple and engaging user interface)',
            'The CTO will oversee project execution and ensure alignment with business objectives.'
          ]
        },
        {
          title: 'Project Timeline & Milestones:',
          content: [
            'The development of SmartFit is planned over six months, divided into key phases:',
            '',
            'Month 1: Research & Planning – Define app features, finalize technology stack, research smartwatch APIs.',
            '',
            'Month 2-3: UI/UX Design & Development – Design user-friendly interfaces, start mobile development.',
            '',
            'Month 4: AI & Notification System Implementation – Develop AI-based workout reminders, integrate push notifications.',
            '',
            'Month 5: Testing & Smartwatch Integration – Conduct extensive user testing, refine features, sync with smartwatches.',
            '',
            'Month 6: Deployment & Optimization – Launch the app, gather user feedback, and release updates.'
          ]
        },
        {
          title: 'Budget & Resources:',
          content: [
            'The estimated budget is not finalized, but the goal is to develop a cost-effective solution using React Native for cross-platform development and Firebase for backend services.'
          ]
        },
        {
          title: 'Risks & Mitigation Strategies:',
          content: [
            'Data Privacy Concerns – Implement strong encryption for user data, comply with GDPR and other relevant policies.',
            '',
            'User Engagement Challenges – AI-driven motivational notifications and personalized reminders to keep users active.',
            '',
            'Smartwatch Compatibility Issues – Early testing with multiple smartwatch brands to ensure smooth integration.'
          ]
        },
        {
          title: 'Success Criteria:',
          content: [
            'At least 10,000 app downloads in the first 6 months',
            '80% of users actively using the app for goal tracking',
            'User engagement increase by 30% through AI-driven notifications',
            'Positive feedback from at least 75% of early adopters'
          ]
        },
        {
          title: 'Approval & Next Steps:',
          content: [
            'This project is awaiting final approval from company leadership. Once approved, the development team will start the Research & Planning phase, followed by design and implementation.'
          ]
        }
      ];
      
      moreSections.forEach(section => {
        // Check if we need a new page
        if (yPos > pdf.internal.pageSize.height - 30) {
          pdf.addPage();
          yPos = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(section.title, margin, yPos);
        yPos += 8;
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        section.content.forEach(line => {
          // Check if we need a new page
          if (yPos > pdf.internal.pageSize.height - 20) {
            pdf.addPage();
            yPos = 20;
          }
          
          if (line) {
            const wrappedText = pdf.splitTextToSize(line, pageWidth - (margin * 2));
            pdf.text(wrappedText, margin, yPos);
            yPos += wrappedText.length * 6;
          } else {
            yPos += 6; // Empty line
          }
        });
        
        yPos += 10; // Space between sections
      });

      pdf.save('project-charter-template.pdf');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const toggleFullResponse = () => {
    setShowFullResponse(!showFullResponse);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Project Charter Generator
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">Instructions</h3>
            <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1">
              <li>• Enter your project details below</li>
              <li>• Include information about:</li>
              <li className="ml-4">- Project objectives and goals</li>
              <li className="ml-4">- Stakeholders and team members</li>
              <li className="ml-4">- Timeline and budget constraints</li>
              <li className="ml-4">- Key deliverables and milestones</li>
            </ul>
            <div className="mt-4">
              <button
                onClick={handleDownloadTemplate}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download Template
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="project-details" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Project Details
              </label>
              <textarea
              id="project-details"
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="w-full h-24 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Enter your project details here..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    Generating Charter...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    Generate Charter
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Project Charter
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    Copy
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <FilePdf className="h-4 w-4 mr-1.5" />
                    Download PDF
                  </button>
                  <button
                    onClick={handleSaveOutput}
                    disabled={savingOutput || (savedOutput && savedOutput.saved)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {savingOutput ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Saving...
                      </>
                    ) : savedOutput && savedOutput.saved ? (
                      'Saved'
                    ) : (
                      'Save Output'
                    )}
                  </button>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mb-4">
                <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                  Project Charter Generated Successfully
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}